/* ============================================================================
   SLURPPED — shared delivery schedule engine
   ----------------------------------------------------------------------------
   The delivery calendar in ONE place for surfaces OUTSIDE the buy zone: today
   the header band's "Next delivery …" line (sections/slurpped-marquee.liquid).

   🔴 THIS IS A DELIBERATE SECOND COPY OF THE DATE MATHS IN
   `sections/slurpped-buy-zone.liquid` (ukNow / cutoffPassed / schedule /
   parseBlackouts). The buy zone is the fail-closed purchase gate and keeps its
   own inline engine on purpose; `test/schedule-parity.test.js` extracts BOTH
   and asserts they return identical day lists across hundreds of thousands of
   (now × config) cases, so the two cannot drift silently. Same arrangement as
   `slurpped-postcode.js`. Change the rules HERE and THERE, then run the test.

   Both read the SAME configuration: Theme settings → "Slurpped · Delivery
   schedule" (first_delivery, delivery_weeks, blackout_weeks). One source.

   Rules, as ruled by Ollie:
   - Order by Sunday 8pm UK wall time (Europe/London, BST/GMT automatic);
     8:00pm exactly is closed, 7:59pm is open.
   - Delivery Thursday (+4) or Friday (+5) of the following week; collection
     is the Wednesday (+3). Offsets from that week's Sunday cutoff.
   - first_delivery is a FLOOR (30 Aug 2026): the rolling window STARTS at the
     launch week instead of this week, and stops applying by itself once that
     week's own cutoff passes. Unparseable = ignored.
   - blackout_weeks: comma-separated Thursdays; each snapped to its week's
     Thursday; malformed dates ignored. A blacked week is skipped.
   - A special (custom.delivery_week + special_weeks) offers ONLY its own run.
   ========================================================================== */
(function (w) {
  'use strict';

  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  var DOWS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var DELIVER_OFFSETS = [4, 5];
  var COLLECT_OFFSETS = [3];

  function ukNow() {
    var o = { year: 0, month: 0, day: 0, hour: 0, minute: 0 };
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', hourCycle: 'h23'
    }).formatToParts(new Date()).forEach(function (p) {
      if (Object.prototype.hasOwnProperty.call(o, p.type)) o[p.type] = parseInt(p.value, 10);
    });
    return o;
  }
  function toUTC(d) { return Date.UTC(d.year, d.month - 1, d.day); }
  function fromUTC(ms) {
    var d = new Date(ms);
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
  }
  function addDays(d, n) { return fromUTC(toUTC(d) + n * 864e5); }
  function dayOfWeek(d) { return new Date(toUTC(d)).getUTCDay(); } // 0 = Sunday
  function cutoffPassed(sunday, now) {
    var s = toUTC(sunday);
    var n = toUTC(now);
    if (n !== s) return n > s;
    return now.hour >= 20; // 8:00pm exactly = closed; 7:59pm still open
  }
  function thursdayOfWeek(d) {
    var monIdx = (dayOfWeek(d) + 6) % 7; // Mon=0 .. Sun=6
    return addDays(d, 3 - monIdx);
  }
  function isoDate(d) {
    return d.year + '-' + ('0' + d.month).slice(-2) + '-' + ('0' + d.day).slice(-2);
  }
  function ordinal(n) {
    var t = n % 10, h = n % 100;
    return n + (t === 1 && h !== 11 ? 'st' : t === 2 && h !== 12 ? 'nd' : t === 3 && h !== 13 ? 'rd' : 'th');
  }
  function longLabel(d) { return DOWS[dayOfWeek(d)] + ' ' + ordinal(d.day) + ' ' + MONTHS[d.month - 1]; }

  // Mirrors the buy zone's readFirstDelivery(): shape check only, leading
  // whitespace forgiven, no calendar validation (a typo can only fall back).
  function parseFirstDelivery(v) {
    var m = String(v || '').match(/^\s*(\d{4})-(\d{2})-(\d{2})/);
    return m ? { year: +m[1], month: +m[2], day: +m[3] } : null;
  }
  // Mirrors the buy zone's parseBlackouts(): strict shape + real calendar
  // date, snapped to that week's Thursday, keyed by ISO date.
  function parseBlackouts(csv) {
    var set = {};
    String(csv || '').split(',').forEach(function (t) {
      var m = t.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return;
      var y = +m[1], mo = +m[2], da = +m[3];
      var probe = new Date(Date.UTC(y, mo - 1, da));
      if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== da) return;
      set[isoDate(thursdayOfWeek({ year: y, month: mo, day: da }))] = true;
    });
    return set;
  }

  // The offer list. cfg = { weeks, firstDelivery, specialWeek, specialRun, collect }
  // Returns [{ date, cutoff }] in order; each cutoff is that week's Sunday.
  function schedule(now, cfg) {
    cfg = cfg || {};
    var offs = cfg.collect ? COLLECT_OFFSETS : DELIVER_OFFSETS;
    var weeks = cfg.weeks || 4;
    var days = [];
    var launchFloor = null;
    var launchCfg = parseFirstDelivery(cfg.firstDelivery);
    if (launchCfg) {
      var fThu = thursdayOfWeek(launchCfg);
      if (!cutoffPassed(addDays(fThu, -4), now)) launchFloor = toUTC(fThu);
    }
    if (cfg.specialWeek) {
      var thu0 = thursdayOfWeek(cfg.specialWeek);
      var runFor = cfg.specialRun || 1;
      for (var sw = 0; sw < runFor; sw++) {
        var thu = addDays(thu0, sw * 7);
        var sun = addDays(thu, -4);
        if (cutoffPassed(sun, now)) continue;
        if (launchFloor !== null && toUTC(thu) < launchFloor) continue;
        for (var oi = 0; oi < offs.length; oi++) days.push({ date: addDays(sun, offs[oi]), cutoff: sun });
      }
      return days;
    }
    var toSun = (7 - dayOfWeek(now)) % 7;
    var sunday = addDays(now, toSun);
    if (toSun === 0 && now.hour >= 20) sunday = addDays(sunday, 7);
    if (launchFloor !== null) {
      var lSun = addDays(fromUTC(launchFloor), -4);
      if (toUTC(lSun) > toUTC(sunday)) sunday = lSun;
    }
    for (var wk = 0; wk < weeks; wk++) {
      var sunW = addDays(sunday, wk * 7);
      for (var o = 0; o < offs.length; o++) days.push({ date: addDays(sunW, offs[o]), cutoff: sunW });
    }
    return days;
  }

  // First bookable day: the schedule with blacked-out weeks dropped, exactly
  // as the buy zone's renderDays filters. Null = nothing bookable in the
  // window (the buy zone shows its shutdown line; callers should hide).
  function nextDelivery(cfg, now) {
    now = now || ukNow();
    var bl = parseBlackouts(cfg && cfg.blackouts);
    var days = schedule(now, cfg);
    for (var i = 0; i < days.length; i++) {
      if (bl[isoDate(addDays(days[i].cutoff, 4))]) continue;
      return days[i];
    }
    return null;
  }

  // "Show until" for a dated message: visible THROUGH that day, gone the next.
  // A malformed date never hides anything (fail-visible for an announcement).
  function isPastDay(ymd, now) {
    var m = String(ymd || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return false;
    var d = { year: +m[1], month: +m[2], day: +m[3] };
    var probe = new Date(toUTC(d));
    if (probe.getUTCFullYear() !== d.year || probe.getUTCMonth() !== d.month - 1 || probe.getUTCDate() !== d.day) return false;
    now = now || ukNow();
    return toUTC(now) > toUTC(d);
  }

  w.SlurppedSchedule = {
    ukNow: ukNow, toUTC: toUTC, fromUTC: fromUTC, addDays: addDays, dayOfWeek: dayOfWeek,
    cutoffPassed: cutoffPassed, thursdayOfWeek: thursdayOfWeek, isoDate: isoDate, longLabel: longLabel,
    parseFirstDelivery: parseFirstDelivery, parseBlackouts: parseBlackouts,
    schedule: schedule, nextDelivery: nextDelivery, isPastDay: isPastDay,
    DELIVER_OFFSETS: DELIVER_OFFSETS, COLLECT_OFFSETS: COLLECT_OFFSETS
  };
})(typeof window !== 'undefined' ? window : this);
